import type { NodeDefinition } from "@/generated";
import type { DynamicNodeConfig } from "@/store/editor-v2";
import type { NodeV2Data } from "@/types/spotflow-v2";
import { flowTypeLabel } from "@/components/SpotflowV2/flowTypes";
import { isFileDataType } from "./typeCompatibility";
import { WHOLE_VAR_REF_PATTERN, parseVarRefId } from "./variableRefs";

export interface BoundObjectField {
  name: string;
  label: string | null;
  dataType: string;
  isList: boolean;
}

interface OutputWithShape {
  name?: string;
  object_fields?:
    | {
        name?: string;
        label?: string | null;
        data_type?: string;
        is_list?: boolean | null;
      }[]
    | null;
}

export interface ShapeGraph {
  nodeDataMap: Map<string, NodeV2Data>;
  nodeRegistry: NodeDefinition[];
  dynamicNodeConfigs: Map<string, DynamicNodeConfig>;
}

/** The object_fields of the output a field's value whole-binds to; empty for
 *  a literal, an interpolated string, or an untyped source. */
export function resolveBoundObjectFields(
  raw: unknown,
  graph: ShapeGraph
): BoundObjectField[] {
  if (typeof raw !== "string") return [];
  const match = WHOLE_VAR_REF_PATTERN.exec(raw);
  if (!match) return [];
  const parsed = parseVarRefId(match[1]);
  if (!parsed) return [];
  const sourceData = graph.nodeDataMap.get(parsed.sourceNodeId);
  if (!sourceData) return [];
  const registryOutputs = graph.nodeRegistry.find(
    (r) => r.type === sourceData.nodeType
  )?.outputs;
  const outputs = (graph.dynamicNodeConfigs.get(parsed.sourceNodeId)?.outputs ??
    registryOutputs ??
    []) as OutputWithShape[];
  const output = outputs.find((o) => o.name === parsed.outputName);
  return (output?.object_fields ?? [])
    .filter((f) => !!f.name)
    .map((f) => ({
      name: f.name as string,
      label: f.label ?? null,
      dataType: f.data_type ?? "str",
      isList: !!f.is_list,
    }));
}

function chipLabelFor(dataType: string, isList: boolean): string | undefined {
  const base = flowTypeLabel(isFileDataType(dataType) ? "File" : dataType);
  if (!base) return undefined;
  return isList ? `List of ${base}` : base;
}

export interface SelectorRow {
  label?: string;
  value?: string;
}

export interface ImpliedShape {
  /** Property names the row-name select offers, existing rows included. */
  names: string[];
  /** Type shown for every property; falls back when the selector has no chip. */
  types: Record<string, string>;
  /** Only the properties whose type the selector can express. A shape type
   *  with no chip (a legacy generic List) is displayed with the fallback but
   *  must never be written into a row. */
  implied: Record<string, string>;
}

export function impliedShape(
  fields: BoundObjectField[],
  rows: SelectorRow[] | undefined,
  values: string[],
  defaultValue: string
): ImpliedShape | undefined {
  if (fields.length === 0) return undefined;
  const names: string[] = [];
  const types: Record<string, string> = {};
  const implied: Record<string, string> = {};
  for (const field of fields) {
    names.push(field.name);
    const chip = chipLabelFor(field.dataType, field.isList);
    const known = chip !== undefined && values.includes(chip);
    types[field.name] = known ? chip : defaultValue;
    if (known) implied[field.name] = chip;
  }
  // Rows created under a previous binding stay selectable (and deletable)
  // instead of rendering as dead entries with no matching option.
  for (const row of rows ?? []) {
    if (row.label && !names.includes(row.label)) names.push(row.label);
  }
  return { names, types, implied };
}

/** Rows with every drifted implied type rewritten, or null when nothing
 *  changed. A row the shape no longer names is left alone: the user repairs
 *  the name, and the backend's stale-selection warning points at it. */
export function healSelectorRows(
  rows: SelectorRow[],
  implied: Record<string, string>
): SelectorRow[] | null {
  let changed = false;
  const healed = rows.map((row) => {
    const type = row.label ? implied[row.label] : undefined;
    if (type === undefined || row.value === type) return row;
    changed = true;
    return { ...row, value: type };
  });
  return changed ? healed : null;
}

interface DictSelectArrayDisplay {
  type?: string;
  values?: string[];
  default_value?: string;
  bound_shape_field?: string | null;
}

export interface ShapeSelector {
  field: string;
  boundField: string;
  values: string[];
  defaultValue: string;
}

/** The property selectors a node type declares: a `dict_select_array` whose
 *  rows pick properties of the object bound to `bound_shape_field`. */
export function shapeSelectors(
  def: NodeDefinition | undefined
): ShapeSelector[] {
  const selectors: ShapeSelector[] = [];
  for (const [field, entry] of Object.entries(
    (def?.config ?? {}) as Record<string, { display?: DictSelectArrayDisplay }>
  )) {
    const display = entry?.display;
    if (display?.type !== "dict_select_array" || !display.bound_shape_field) {
      continue;
    }
    selectors.push({
      field,
      boundField: display.bound_shape_field,
      values: display.values ?? [],
      defaultValue: display.default_value ?? "Text",
    });
  }
  return selectors;
}

/** The node's config with every property selector's drifted implied types
 *  rewritten to follow the bound object's shape, or null when nothing
 *  changed. The type select is disabled because the type is implied by the
 *  shape, so a row retyped upstream could never be repaired by hand. */
export function reconcileBoundShapeRows(
  nodeConfig: Record<string, unknown>,
  def: NodeDefinition | undefined,
  graph: ShapeGraph
): Record<string, unknown> | null {
  let next: Record<string, unknown> | null = null;
  for (const selector of shapeSelectors(def)) {
    const rows = nodeConfig[selector.field];
    if (!Array.isArray(rows)) continue;
    const fields = resolveBoundObjectFields(
      nodeConfig[selector.boundField],
      graph
    );
    const shape = impliedShape(
      fields,
      rows as SelectorRow[],
      selector.values,
      selector.defaultValue
    );
    if (!shape) continue;
    const healed = healSelectorRows(rows as SelectorRow[], shape.implied);
    if (healed === null) continue;
    next = { ...(next ?? nodeConfig), [selector.field]: healed };
  }
  return next;
}
